import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "polling", kind: "", tries: 0 });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", kind: "" }); return; }
    let cancelled = false;
    const poll = async (tries) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "ok", kind: data.kind || "" });
          refresh();
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setState({ status: "failed", kind: data.kind || "" });
          return;
        }
        if (tries >= 10) {
          setState({ status: "pending", kind: data.kind || "" });
          return;
        }
        setTimeout(() => poll(tries + 1), 2000);
      } catch (e) {
        setState({ status: "error", kind: "" });
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  const isPro = state.kind === "pro_lifetime";
  const isBoost = state.kind === "boost_submission";
  const isTip = state.kind === "tip";

  return (
    <div className="max-w-xl mx-auto px-6 py-20" data-testid="payment-success-page">
      <div className="border-2 border-black bg-white p-8 md:p-10 text-center">
        {state.status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-black/40 animate-spin" strokeWidth={1.5} />
            <div className="mt-4 font-mono text-xs uppercase tracking-widest text-black/50">Confirming payment…</div>
          </>
        )}
        {state.status === "ok" && (
          <>
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-700 mb-3">
              <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} /> Payment received
            </div>
            <h1 className="font-serif font-black text-4xl md:text-5xl tracking-tighter leading-tight" data-testid="success-headline">
              {isPro && <>Welcome to <span className="italic font-normal">Pro.</span></>}
              {isBoost && <>Your deal is <span className="italic font-normal">boosted.</span></>}
              {isTip && <>Thanks for <span className="italic font-normal">the tip.</span></>}
              {!isPro && !isBoost && !isTip && "Thank you."}
            </h1>
            <p className="text-black/60 mt-4 text-sm">
              {isPro && "Your Pro perks are already active — earlier digests, PRO byline, priority review."}
              {isBoost && "Your submission just jumped to the top of the queue and will be auto-featured on approval."}
              {isTip && "The contributor's tip counter has been updated."}
              {!isPro && !isBoost && !isTip && "The transaction has been recorded."}
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <Link to="/" className="bg-black text-white px-6 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946]" data-testid="success-home">
                Back to DealHub
              </Link>
            </div>
          </>
        )}
        {state.status === "pending" && (
          <>
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-amber-700 mb-3">
              Still processing
            </div>
            <h1 className="font-serif font-black text-3xl tracking-tighter">Payment is taking longer than usual</h1>
            <p className="text-black/60 mt-4 text-sm">We&rsquo;ll email you as soon as it confirms.</p>
          </>
        )}
        {(state.status === "failed" || state.status === "error") && (
          <>
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-3">
              <XCircle className="w-3.5 h-3.5" strokeWidth={2} /> Payment not completed
            </div>
            <h1 className="font-serif font-black text-3xl tracking-tighter">Something went sideways</h1>
            <p className="text-black/60 mt-4 text-sm">No charge was made. Try again from the pricing page.</p>
            <Link to="/pricing" className="inline-block mt-6 bg-black text-white px-6 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#E63946]" data-testid="retry-pricing">
              Back to pricing
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
